<template>
    <div class="payroll-leaves-calculation-container" v-if="showLeavesCalculation">
        <div class="payroll-leaves-calculation-panel">
            <div class="payroll-emp-info-panel-header">
                <span class="icon">📝</span>
                <h2>Leaves Calculation</h2>
            </div>
            <div class="payroll-leaves-calculation-panel-body">
                <div class="payroll-emp-info-form-group">
                    <label>Remaining Leaves
                        <!-- <ResetIconButton @reset="() => forceResetField('remainingLeaves')" /> -->
                    </label>
                    <input type="number" v-model.number="remainingLeaves" :disabled="true"
                        @input="remainingLeavesManuallyUpdated = true" placeholder="0" step="0.5" min="0"
                        @wheel="$event.target.blur()" class="payroll-disabled-field" />
                </div>
                <div class="payroll-emp-info-form-group">
                    <div class="input-with-reset">
                        <label>Carry Forward Leaves</label>
                        <ResetIconButton @reset="() => forceResetField('carryForwardDays')" />
                        <div>
                            <span class="required-hours-tooltip-wrapper" @mouseover="isCarryForwardInfoVisible = true"
                                @mouseleave="isCarryForwardInfoVisible = false">
                                <i class="fa fa-info-circle" style="color: yellowgreen;"></i>
                                <div v-if="isCarryForwardInfoVisible" style="width: 230px;" class="required-hours-tooltip-box">
                                    <b>Carry Forward Rules </b>
                                    <div><b>(December only):</b></div>
                                    <ul>
                                        <li v-for="(rule, idx) in carryForwardRulesList" :key="'cf-rule-' + idx">
                                            {{ rule }}
                                        </li>
                                        <li>Less than 2 leaves → 0 days carried forward</li>
                                    </ul>
                                </div>
                            </span>
                        </div>
                    </div>
                    <input type="number" v-model.number="carryForwardDays" :disabled="isDisabled"
                        @input="carryForwardManuallyUpdated = true" placeholder="0" step="0.5"
                        @wheel="$event.target.blur()" />
                </div>
                <div class="payroll-emp-info-form-group">
                    <div class="input-with-reset">
                        <label>Leaves Encashment</label>
                        <ResetIconButton @reset="() => forceResetField('leavesEncashmentDays')" />
                        <span class="required-hours-tooltip-wrapper" @mouseover="isEncashmentInfoVisible = true"
                            @mouseleave="isEncashmentInfoVisible = false">
                            <i class="fa fa-info-circle" style="color: yellowgreen;"></i>
                            <div v-if="isEncashmentInfoVisible" class="required-hours-tooltip-box" style="height: 170px; width: 230px;">
                                <b>Encashment Rules</b>
                                <div><b>(December only):</b></div>
                                <ul>
                                    <li v-for="(rule, idx) in encashmentRulesList" :key="'enc-rule-' + idx">
                                        {{ rule }}
                                    </li>
                                    <li>Less than 1 leave → 0 days encashed</li>
                                </ul>
                            </div>
                        </span>
                    </div>
                    <input type="number" v-model.number="leavesEncashmentDays"
                        :disabled="isDisabled || this.month === 'June'" @input="onLeavesEncashmentInput" placeholder="0"
                        @wheel="$event.target.blur()" :class="this.month === 'June' ? 'payroll-disabled-field' : ''" />
                </div>
                <div class="payroll-emp-info-form-group">
                    <div class="input-with-reset">
                        <label>New Leave Quota</label>
                        <ResetIconButton @reset="() => forceResetField('updatedLeaveQuota')" />
                    </div>
                    <input type="number" v-model.number="updatedLeaveQuota" :disabled="isDisabled"
                        @input="updatedLeaveQuotaManuallyUpdated = true" placeholder="0" step="0.5"
                        @wheel="$event.target.blur()" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import ResetIconButton from "../../../UIComponents/Buttons/ResetIconButton.vue";
import { leaveEncashmentDecRule, leavesCarryForwardDecRule } from "../../../../assets/config/constants";

export default {
    name: "LeavesCalculation",
    components: {
        ResetIconButton
    },
    props: {
        isAdmin: {
            type: Boolean,
            default: false
        },
        employee: {
            type: Object,
            required: true
        },
        initialValues: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            remainingLeaves: 0,
            carryForwardDays: 0,
            updatedLeaveQuota: 0,
            perDaySalary: 0,
            leavesEncashmentDays: 0,
            annualLeavesEncashmentDays: 0,
            leavesEncashmentAmount: 0,
            month: this.employee.month,

            leavesEncashmentManuallyUpdated: false,
            remainingLeavesManuallyUpdated: false,
            carryForwardManuallyUpdated: false,
            updatedLeaveQuotaManuallyUpdated: false,
            isCarryForwardInfoVisible: false,
            isEncashmentInfoVisible: false,
            constants : this.$store.state.auth.constants || {}
        };
    },
    computed: {
        showLeavesCalculation() {
            const month = this.employee.month;
            const status = this.employee.employeeStatus;
            const isCorrectMonth = month === 'December' || month === 'June';
            const isActiveEmployee = status !== 'Resigned' && status !== 'Probation';
            return isCorrectMonth && isActiveEmployee;
        },
        selectedEmployee() {
            return this.$store.state.payroll.selectedEmployeePayroll || {};
        },
        isDisabled() {
            return this.$store.state.auth.user.role !== "admin";
        },
        carryForwardRulesList() {
            const grouped = {};

            // Group keys by their value (e.g., { "1": [2, 3], "2": [4, 5] })
            for (const [key, value] of Object.entries(leavesCarryForwardDecRule)) {
                if (!grouped[value]) grouped[value] = [];
                grouped[value].push(key);
            }

            // Create human-readable lines
            return Object.entries(grouped).map(([carry, days]) => {
                const range = days.join(' or ');
                return `${range} leaves remaining → ${carry} day${carry > 1 ? 's' : ''} carried forward`;
            });
        },
        encashmentRulesList() {
            const grouped = {};

            // Group keys by their encashment value
            for (const [key, value] of Object.entries(leaveEncashmentDecRule)) {
                if (!grouped[value]) grouped[value] = [];
                grouped[value].push(key);
            }

            return Object.entries(grouped).map(([encash, days]) => {
                const range = days.join(' or ');
                return `${range} leaves remaining → ${encash} day${encash > 1 ? 's' : ''} encashed`;
            });
        }
    },
    methods: {
        onLeavesEncashmentInput() {
            this.leavesEncashmentManuallyUpdated = true;
        },

        updateLeavesData() {
            if (!this.selectedEmployee) return;
            const leavesQuota = this.selectedEmployee.leavesQuota || {};

            if (!this.remainingLeavesManuallyUpdated) {
                this.remainingLeaves = Number(this.selectedEmployee.leavesAllowed) - Number(this.selectedEmployee.DBLeavesTaken) - Number(this.selectedEmployee.leavesDeductionDays) || 0;
            }

            this.perDaySalary = this.selectedEmployee.perDaySalary || 0;

            if (!this.carryForwardManuallyUpdated) {
                        this.carryForwardDays = leavesQuota.carryForwardDays || "";
            }

            if (!this.updatedLeaveQuotaManuallyUpdated) {
                        this.updatedLeaveQuota = leavesQuota.updatedLeaveQuota || "";
            }

            if (this.month === "June") {
                if (!this.updatedLeaveQuotaManuallyUpdated) {
                    this.updatedLeaveQuota = Number(this.calculateJuneLeaveQuota() || leavesQuota.updatedLeaveQuota) || "";
                }
                if (!this.carryForwardManuallyUpdated) {
                    this.carryForwardDays = this.calculateJuneCarryForward() || leavesQuota.carryForwardDays || "";
                }
            } else if (this.month === "December") {
                        const calculatedEncashment = this.calculateEncashment() || leavesQuota.leavesEncashmentDays || "";

                if (!this.carryForwardManuallyUpdated) {
                            this.carryForwardDays = this.calculateDecCarryForward() || leavesQuota.carryForwardDays || "";
                }

                if (!this.leavesEncashmentManuallyUpdated) {
                    const calculatedEncashment = this.calculateEncashment();
                    this.leavesEncashmentDays = calculatedEncashment;
                    this.annualLeavesEncashmentDays = calculatedEncashment;
                }

                if (!this.updatedLeaveQuotaManuallyUpdated) {
                            this.updatedLeaveQuota = Number(this.calculateUpdatedLeaveQuota() || leavesQuota.updatedLeaveQuota) || "";
                }
            }
        },
        forceResetField(field) {
            if (field === "leavesEncashmentDays") {
                this.leavesEncashmentDays = this.calculateEncashment();
                this.leavesEncashmentManuallyUpdated = false;
            } else if (field === "carryForwardDays") {
                this.carryForwardDays = this.month === "December"
                    ? this.calculateDecCarryForward()
                    : this.calculateJuneCarryForward();
                this.carryForwardManuallyUpdated = false;
            } else if (field === "updatedLeaveQuota") {
                this.updatedLeaveQuota = this.calculateUpdatedLeaveQuota();
                this.updatedLeaveQuotaManuallyUpdated = false;
            } else if (field === "remainingLeaves") {
                this.remainingLeaves = this.initialValues.remainingLeaves || 0;
                this.remainingLeavesManuallyUpdated = false;
            }
        },

        resetCarryForward() {
            if (this.carryForwardDays === "" || this.carryForwardDays === null) {
                this.carryForwardDays = this.calculateDecCarryForward();
                this.carryForwardManuallyUpdated = false;
            }
        },
        resetLeaveQuota() {
            if (this.updatedLeaveQuota === "" || this.updatedLeaveQuota === null) {
                this.updatedLeaveQuota = this.calculateUpdatedLeaveQuota();
                this.updatedLeaveQuotaManuallyUpdated = false;
            }
        },
        calculateJuneLeaveQuota() {
            const emp = this.selectedEmployee
            const leavesAllowed = Number(emp.leavesAllowed || 0)
            const leavesTaken = Number(emp.DBLeavesTaken || 0)
            const leavesAvailable = Number(emp.leavesAvailable || 0)
            const remainingLeaves = leavesAllowed - leavesTaken
            const newAllowedLeaves = leavesAvailable - leavesAllowed + remainingLeaves
            const updatedLeaves = newAllowedLeaves
            return updatedLeaves
        },
        calculateEncashment() {
            if (this.month !== "December") return 0;

            const remaining = Math.min(Math.floor(this.remainingLeaves), 5);
            const encashmentValue = leaveEncashmentDecRule[remaining] || "";

            if (!this.leavesEncashmentManuallyUpdated) {
                this.leavesEncashmentDays = encashmentValue;
                this.annualLeavesEncashmentDays = encashmentValue;
            }

            return encashmentValue;
        },
        calculateDecCarryForward() {
            if (this.month !== "December") return 0;

            const remaining = Math.min(Math.floor(this.remainingLeaves), 5);
            return leavesCarryForwardDecRule[remaining] || "";
        },
        calculateJuneCarryForward() {
            if (this.month !== "June") return 0;
            const emp = this.selectedEmployee
            const remaininingAllowedLeaves = Number(emp.leavesAllowed || 0) - Number(emp.DBLeavesTaken || 0)
            return remaininingAllowedLeaves
        },

        calculateUpdatedLeaveQuota() {
            if (this.month === "December") {
                if (this.remainingLeavesManuallyUpdated) {
                    if (Number(this.remainingLeaves) <= 5) {
                        const baseQuota = this.remainingLeaves;
                        const carryForwardLeaves = this.calculateDecCarryForward();
                        return Number(baseQuota) + Number(carryForwardLeaves);
                    } else {
                        // Keep the last known quota, don't change
                        return this.updatedLeaveQuota;
                    }
                } else {
                    return Number(this.constants.leavesAvailable) + this.carryForwardDays;
                }
            }

            if (this.month === "June") {
                if (this.remainingLeavesManuallyUpdated) {
                    const baseQuota = this.remainingLeaves;
                    const carryForwardLeaves = this.calculateJuneCarryForward();
                    return Number(baseQuota) + Number(carryForwardLeaves);
                }
                console.log("refactor", this.selectedEmployee.leavesAvailable, this.remainingLeaves);
                return Number(this.remainingLeaves) + (Number(this.selectedEmployee.leavesAvailable) / 2);
            }

            return this.updatedLeaveQuota;
        },

        syncToEmployee() {
            const leavesData = this.getUpdatedLeavesData();

            const updatedLeavesQuota = {
                remainingLeaves: Number(this.remainingLeaves) || 0,
                carryForwardDays: Number(this.carryForwardDays) || 0,
                updatedLeaveQuota: Number(this.updatedLeaveQuota) || 0,
                leavesEncashmentDays: Number(this.leavesEncashmentDays) || 0,
            };

            const updated = Object.assign({}, this.employee, {
                annualLeavesEncashmentDays: Number(this.leavesEncashmentDays),
                leavesQuota: updatedLeavesQuota,  // Keeping the leavesQuota data inside the object
            });

            for (let key in leavesData) {
                if (leavesData.hasOwnProperty(key)) {
                    updated[key] = leavesData[key];
                }
            }

            this.$emit("update-leaves", updated); // emit updated employee data
        },
        getUpdatedLeavesData() {
            const leavesQuota = this.selectedEmployee.leavesQuota
            const payrollMonth = this.month

            let data = {
                leavesEncashmentDays: this.leavesEncashmentDays,
                updatedLeaveQuota: this.updatedLeaveQuota,
                remainingLeaves: this.remainingLeaves,
                carryForwardDays: this.carryForwardDays,
                annualLeavesEncashmentDays: this.leavesEncashmentDays
            };

            if (payrollMonth === "June") {
                data.midYearLeaves = {
                    leavesAvailable: this.updatedLeaveQuota
                };
            } else if (payrollMonth === "December") {
                data.annualLeaves = {
                    leavesAvailable: this.updatedLeaveQuota / 2,
                    leavesAllowed: this.updatedLeaveQuota
                };
            }

            return data;
        },

    },
    watch: {
        selectedEmployee: {
            handler() {
                this.updateLeavesData();
            },
            deep: true,
            immediate: true
        },
        employee: {
            immediate: true,
            deep: true,
            handler: function (newVal) {
                this.updateLeavesData();
            },
        },

        remainingLeaves(val) {
            this.remainingLeaves = val;

            if (!this.leavesEncashmentManuallyUpdated && this.month === "December") {
                this.leavesEncashmentDays = this.calculateEncashment();
                this.annualLeavesEncashmentDays = this.leavesEncashmentDays;
            }

            if (!this.carryForwardManuallyUpdated) {
                this.carryForwardDays = this.month === "December"
                    ? this.calculateDecCarryForward()
                    : this.calculateJuneCarryForward();
            }

            if (!this.updatedLeaveQuotaManuallyUpdated) {
                this.updatedLeaveQuota = this.calculateUpdatedLeaveQuota();
            }

            this.syncToEmployee();
        },
        carryForwardDays(val) {
            this.syncToEmployee();
        },
        updatedLeaveQuota(val) {
            this.syncToEmployee();
        },
        leavesEncashmentDays(val) {
            this.syncToEmployee();
        },
    },
    mounted() {
        this.syncToEmployee();

    },
};
</script>

<style scoped>
.payroll-leaves-calculation-container {
    padding: 1rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    background: white;
    width: 100%;
}

.payroll-leaves-calculation-panel-body {
    margin-top: 20px;
}
</style>


// WEBPACK FOOTER //
// src/components/PayrollManagement/PayrollDetails/SalaryCalculation/LeavesCalculation.vue